import {
  EntryZoneCategory,
  FiltersActions,
  FilterSectionType,
  FiltersRequest,
  FilterType,
  FixMeLater,
  RawFiltersData,
} from "@shared/types";
import { HOT_TRADES_HELPER_DESCRIPTION } from "@shared/common/constants";

export const FILTER_TYPES_DATA: Record<
  FilterType,
  {
    queryKey: string;
    title: string;
    setActionName?: keyof FiltersActions;
    filterSectionType: FilterSectionType;
    constantsKey?: keyof RawFiltersData["constants"];
    showSingleOption?: boolean;
    showEmptyOptions?: boolean;
    defaultValue?: FixMeLater;
  }
> = {
  [FilterType.EXCHANGES]: {
    queryKey: "exchanges",
    title: "Exchanges",
    setActionName: "setExchangesFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Exchanges",
    showSingleOption: true,
  },
  [FilterType.SYMBOLS]: {
    queryKey: "symbols",
    title: "Symbols",
    setActionName: "setSymbolsFilter",
    filterSectionType: FilterSectionType.MULTI_SELECT,
  },
  [FilterType.CLIENTS]: {
    queryKey: "accounts",
    title: "Accounts",
    setActionName: "setClientsFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Accounts",
    showSingleOption: true,
  },
  [FilterType.STATUS]: {
    queryKey: "status",
    title: "Status",
    setActionName: "setStatusesFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.LONG_SHORT]: {
    queryKey: "direction",
    title: "Direction",
    setActionName: "setLongShortFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Long/Short",
  },
  [FilterType.SOURCE_NAME]: {
    queryKey: "source-name",
    title: "Source Name",
    setActionName: "setSourceFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Source New",
  },
  [FilterType.SIGNAL_STATUS]: {
    queryKey: "signal-status",
    title: "Signal Status",
    setActionName: "setSignalStatusFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.FOLLOW_STATUS]: {
    queryKey: "follow-status",
    title: "Follow Status",
    setActionName: "setFollowStatusFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.SOURCE_TYPE]: {
    queryKey: "source-type",
    title: "Source Type",
    setActionName: "setSourceTypeFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Source Type",
  },
  [FilterType.MARGIN_TYPE]: {
    queryKey: "margin-type",
    title: "Margin Type",
    setActionName: "setMarginTypeFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Margin Type",
  },
  [FilterType.TRADE_TYPE]: {
    queryKey: "trade-type",
    title: "Trade Type",
    setActionName: "setTradeTypeFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
    constantsKey: "Trade Type",
  },
  [FilterType.LEVERAGE]: {
    queryKey: "leverage",
    title: "Leverage",
    setActionName: "setLeverageFilter",
    filterSectionType: FilterSectionType.SLIDER,
  },
  [FilterType.CONSECUTIVE_ACTIVE_DAYS]: {
    queryKey: "active-days",
    title: "Days Running",
    setActionName: "setConsecutiveActiveDaysFilter",
    filterSectionType: FilterSectionType.SLIDER,
  },
  [FilterType.TIME_FRAME]: {
    queryKey: "time-frame",
    title: "Time Range",
    setActionName: "setTimeFrameFilter",
    filterSectionType: FilterSectionType.TIME_RANGE,
    showEmptyOptions: true,
  },
  [FilterType.GROUPS]: {
    queryKey: "groups",
    title: "Groups",
    setActionName: "setGroupsFilter",
    filterSectionType: FilterSectionType.RADIO,
  },
  [FilterType.MULTI_GROUPS]: {
    queryKey: "multi-groups",
    title: "Groups",
    setActionName: "setMultiGroupsFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.AMOUNT_TYPE]: {
    queryKey: "amount-type",
    title: "Amount Type",
    setActionName: "setAmountType",
    filterSectionType: FilterSectionType.RADIO,
  },
  [FilterType.PRICE_RANGE]: {
    queryKey: "price-range",
    title: "Price Range",
    setActionName: "setPriceFilter",
    filterSectionType: FilterSectionType.PRICE_RANGE,
  },
  [FilterType.USD_PRICE_RANGE]: {
    queryKey: "usd-price",
    title: "Monthly USD Price",
    setActionName: "setUsdPriceRangeFilter",
    filterSectionType: FilterSectionType.SLIDER,
  },
  [FilterType.AVG_NUM_SIGNALS]: {
    queryKey: "avg-signals",
    title: "Avg. Signals (Per Week)",
    setActionName: "setAvgNumSignalsFilter",
    filterSectionType: FilterSectionType.SLIDER,
  },
  [FilterType.AVG_RATING]: {
    queryKey: "avg-rating",
    title: "Avg. Customer Rating",
    setActionName: "setAvgRatingFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
  [FilterType.IS_HOT]: {
    queryKey: "is-hot",
    title: "Hot Trades",
    setActionName: "setIsHotFilter",
    filterSectionType: FilterSectionType.BOOLEAN,
    defaultValue: false,
  },
  [FilterType.VALID_BOT_AMOUNT]: {
    queryKey: "hide-insufficient-funds",
    title: "Bot Amount",
    setActionName: "setValidBotAmountFilter",
    filterSectionType: FilterSectionType.BOOLEAN,
    defaultValue: true,
  },
  [FilterType.TAGS]: {
    queryKey: "tags",
    title: "Tags",
    setActionName: "setTagsFilter",
    filterSectionType: FilterSectionType.TAGS,
    defaultValue: [],
  },
  [FilterType.BACKTESTING_STATUS]: {
    queryKey: "backtesting-status",
    title: "Status",
    setActionName: "setBacktestingStatusFilter",
    filterSectionType: FilterSectionType.CHECKBOX,
  },
};

export const SINGLE_FILTERS_ROW_BREAKPOINT = "800px";

export const FILTERS_HELPER_TOOLTIP_DATA: {
  [key in FilterType]?: any; // TODO: fix this to point to helper tooltip props
} = {
  [FilterType.VALID_BOT_AMOUNT]: {
    description:
      "This quick filter allows you to remove all the public bots that require an available amount that is larger that your current available funds as to only present you with public bots that you can easily copy.",
    avoFieldName: "Valid Bot Amount",
    title: "Bot Amount",
  },
  [FilterType.IS_HOT]: {
    description: HOT_TRADES_HELPER_DESCRIPTION,
    avoFieldName: "Is Hot",
    title: "Hot Trades",
  },
};

export const SIGNAL_STATUS_TO_DISPLAY: Record<EntryZoneCategory, string> = {
  [EntryZoneCategory.ABOVE_ENTRY_ZONE]: "Above Entries",
  [EntryZoneCategory.TOP_ENTRY_ZONE]: "Top Entry Zone",
  [EntryZoneCategory.MID_ENTRY_ZONE]: "Middle Entry Zone",
  [EntryZoneCategory.BOTTOM_ENTRY_ZONE]: "Bottom Entry Zone",
  [EntryZoneCategory.BELOW_ENTRY_ZONE]: "Below Entries",
};

export const FILTER_TYPE_TO_REQUEST_KEY: {
  [key in FilterType]?: keyof FiltersRequest;
} = {
  [FilterType.MARGIN_TYPE]: "margin_type",
  [FilterType.CLIENTS]: "client_ids",
  [FilterType.TRADE_TYPE]: "trade_types",
  [FilterType.LONG_SHORT]: "reverse",
  [FilterType.TIME_FRAME]: "close_timestamp",
  [FilterType.LEVERAGE]: "multiplier",
  [FilterType.SOURCE_NAME]: "source",
} as const;
